/**
 * One way for a button to say it heard you.
 *
 * Alan, 2026-08-10: "when click the tab if loading will actually shows loading
 * with a open circle loading not just sit there and wait… this should apply for
 * every loading situation. make a easy loading ui displaying, dont make it hard to
 * understand or too much motion."
 *
 * Pressing Sign in, or Done on a Task, left the button exactly as it was while the
 * request ran. Nothing moved, so the natural read is that the click missed, and
 * the natural response is to press again.
 *
 * The whole vocabulary is one open ring turning inside the button that was
 * pressed, in place of its label. Not a full-screen overlay, not a skeleton, not a
 * progress bar - the thing you touched is the thing that answers, which is the
 * only mapping nobody has to learn.
 *
 * Wired once, globally, on submit. Every form in the product gets it without
 * knowing about it, which is what "every loading situation" has to mean if it is
 * not going to rot the first time someone adds a page.
 */

/** How long a stuck button waits before it gives itself back. */
export const BUSY_RELEASE_MS = 20_000;

export function setButtonBusy(button, busy = true) {
  if (!button || typeof button.setAttribute !== 'function') return false;
  if (!busy) {
    delete button.dataset.busy;
    button.removeAttribute('aria-busy');
    button.disabled = button.dataset.busyWasDisabled === 'true';
    delete button.dataset.busyWasDisabled;
    return false;
  }
  if (button.dataset.busy === 'true') return true;
  // Remembered rather than assumed: a button that was already disabled for its own
  // reasons must not come back enabled when the work finishes.
  button.dataset.busyWasDisabled = String(button.disabled === true);
  button.dataset.busy = 'true';
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;
  return true;
}

export function isButtonBusy(button) {
  return button?.dataset?.busy === 'true';
}

/** The button a form submission should light up. */
export function submitButtonFor(form) {
  if (!form?.querySelector) return null;
  return form.querySelector('button[type="submit"]')
    || form.querySelector('button:not([type="button"]):not([type="reset"])')
    || null;
}

/**
 * Every submit in the document, without each page opting in.
 *
 * A button is released when the person touches the form again - a rejected
 * submission always ends with them typing or picking something - and by a backstop
 * timer, so a request that never resolves cannot strand the control forever.
 */
export function mountGlobalBusyButtons(documentRef = globalThis.document, {
  releaseMs = BUSY_RELEASE_MS,
} = {}) {
  if (!documentRef?.addEventListener) return () => {};
  const timers = new Map();

  const release = (button) => {
    if (!button) return;
    const timer = timers.get(button);
    if (timer) {
      globalThis.clearTimeout?.(timer);
      timers.delete(button);
    }
    setButtonBusy(button, false);
  };

  const onSubmit = (event) => {
    const button = submitButtonFor(event.target);
    if (!button || isButtonBusy(button)) return;
    setButtonBusy(button, true);
    timers.set(button, globalThis.setTimeout?.(() => release(button), releaseMs));
  };

  const onInteract = (event) => {
    const form = event.target?.closest?.('form');
    if (!form) return;
    release(submitButtonFor(form));
  };

  documentRef.addEventListener('submit', onSubmit, true);
  documentRef.addEventListener('input', onInteract, true);
  documentRef.addEventListener('change', onInteract, true);
  return () => {
    documentRef.removeEventListener('submit', onSubmit, true);
    documentRef.removeEventListener('input', onInteract, true);
    documentRef.removeEventListener('change', onInteract, true);
    for (const button of [...timers.keys()]) release(button);
  };
}
